import isString from 'lodash/isString';
import type { FilterParamsType, QueryDataObjectType } from 'app/shared/types/QueryDataObjectType';

const getFilterString = (filterName: string, filterValue: string | number | null) => {
  if (filterValue) {
    if (isString(filterValue)) {
      return `${filterName}: "${filterValue}",`;
    } else {
      return `${filterName}: ${filterValue},`;
    }
  }
  return '';
};

const geoFields = `
        geo {
            lat
            lon
        }
`;

const addressDetailsFields = `
        floorplans {
            zpid
            numBeds
            numFullBaths
            zestimate {
                rentZestimate
                rentZestimateRangeLow
                rentZestimateRangeHigh
            }
            minSqft
            maxSqft
            address {
                street
                city
                state
                zip
                unit
            }
        }
`;

const marketSummaryFields = `
        marketSummary {
            url
            areaName
            beds
            summary {
                medianRent
                monthlyChange
                yearlyChange
                avgDaysOnMarket
                availableRentals
            }
        }
`;

const comparablesFields = (filterParams: FilterParamsType) => {
  const {
    activeTypes = null,
    activatedDays = null,
    amenities = null,
    bedrooms = null,
    deactivatedDays = null,
    distanceInMiles = null,
    laundry = null,
    limit = null,
    offset = null,
    orderBy = 'lowPrice',
    pets = null,
    price = 0,
    propertyTypes = null,
  } = filterParams;
  return `
        similarFloorplans (
            filter: {
                ${getFilterString('activeTypes', activeTypes)}
                ${getFilterString('activatedDays', activatedDays)}
                ${getFilterString('deactivatedDays', deactivatedDays)}
                ${getFilterString('propertyTypes', propertyTypes)}
                ${getFilterString('bedrooms', bedrooms)}
                ${getFilterString('pets', pets)}
                ${getFilterString('laundry', laundry)}
                ${getFilterString('amenities', amenities)}
                ${getFilterString('orderBy', orderBy)}
                ${getFilterString('distanceInMiles', distanceInMiles)}
                ${getFilterString('price', price)}
                ${getFilterString('limit', limit)}
                ${getFilterString('offset', offset)}
            }
        ) {
            listingRanking
            highestPriceIncluded
            lowestPriceIncluded
            boundingBox{
                minLat
                maxLat
                minLon
                maxLon
            }
            floorplans{
                aliasEncoded
                floorplanGroupId
                active
                zpid
                numBeds
                numFullBaths
                numPartialBaths
                numUnitsAvailable
                propertyUrl
                lowPrice
                highPrice
                distanceInMiles
                minSqft
                maxSqft
                address{
                    street
                }
                geo{
                    lat
                    lon
                    quad
                }
                amenities{
                    laundry{
                        inUnit
                        shared
                    }
                    parking{
                        valet
                        garage
                        offStreet
                    }
                    hvac{
                        airConditioning
                        heating
                    }
                    pets{
                        cats
                        largeDogs
                        smallDogs
                    }
                }
                history{
                    lastUpdated{
                        date
                        ago
                    }
                    deactivated{
                        date
                        ago
                    }
                }
                photos{
                    listingPhotos{
                        medium
                    }
                }
            }
        }
    `;
};

export const getFullQuery = (params: QueryDataObjectType) => {
  const { street, city, state, zip, unit, ...filterParams } = params;

  return `{
        byAddress(
            street: "${street}"
            city: "${city}"
            state: "${state}"
            zip: "${zip}"
            unit: "${unit}"
        ) {
            ${geoFields}
            ${addressDetailsFields}
            ${marketSummaryFields}
            ${comparablesFields(filterParams)}
        }
    }`.replace(/(^[ \t]*\n)/gm, ''); // finally remove empty lines
};

export const getCompsOnlyQuery = (params: QueryDataObjectType) => {
  const { street, city, state, zip, unit, ...filterParams } = params;

  return `{
        byAddress(
            street: "${street}"
            city: "${city}"
            state: "${state}"
            zip: "${zip}"
            unit: "${unit}"
        ) {
            ${geoFields}
            ${comparablesFields(filterParams)}
        }
    }`.replace(/(^[ \t]*\n)/gm, ''); // finally remove empty lines
};

export const getCompareRentQuery = (params: {
  resourceId: string;
  otherResourceId: string;
  bedrooms?: string;
  propertyTypes?: string;
}) => {
  const { resourceId, otherResourceId, bedrooms, propertyTypes } = params;
  return `{
        marketPage(resourceId: "${resourceId}", beds: "${bedrooms ?? ''}", propertyTypes: "${propertyTypes ?? ''}") {
            rentCompare(resourceId: "${otherResourceId}") {
                medianRent
                areaName
            }
          }
    }`.replace(/(^[ \t]*\n)/gm, ''); // finally remove empty lines;
};

export const getMarketTrendsQuery = (params: {
  resourceId: string;
  bedrooms?: string;
  propertyTypes?: string;
  otherResourceId?: string;
}) => {
  const { resourceId, bedrooms, propertyTypes, otherResourceId } = params;
  return `{
        marketPage(resourceId: "${resourceId}", beds: "${bedrooms ?? ''}", propertyTypes: "${propertyTypes ?? ''}") {
            date
            zillowRentalsSrpUrl
            areaName
            areaType
            summary {
                medianRent
                monthlyChange
                yearlyChange
                avgDaysOnMarket
                availableRentals
            }
            rentHistogram {
                minPrice
                maxPrice
                priceAndCount {
                    price
                    count
                }
            }
            rentCompare(resourceId: "${otherResourceId ?? ''}") {
                medianRent
                areaName
            }
            marketTemperature {
                temperature
            }
            medianRentPriceOverTime {
                currentYear {
                  month
                  year
                  price
                }
                prevYear {
                  month
                  year
                  price
                }
            }
            nearByAreas {
              areaId
              resourceName
              name
              city
              state
              areaType
            }
            zipcodesInCity {
              areaId
              resourceName
              name
              city
              state
              areaType
            }
            nearbyAreaTrends {
              date
              areaName
              resourceId
              medianRent
              medianRentPriceOverTime {
                month
                year
                price
              }
            }
        }
    }`.replace(/(^[ \t]*\n)/gm, ''); // finally remove empty lines;
};

export const getFAQParamsQuery = (resourceId: string) => {
  return `{
        marketPage(resourceId: "${resourceId}", beds: "", propertyTypes: "") {
            areaName
            summary {
                medianRent
                monthlyChange
                yearlyChange
                availableRentals
            }
            rentHistogram {
                minPrice
                maxPrice
            }
        }
    }`.replace(/(^[ \t]*\n)/gm, ''); // finally remove empty lines;
};

export const getTopMarketsQuery = (params: { bedrooms?: string; propertyTypes?: string }) => {
  const { bedrooms, propertyTypes } = params;
  return `{
        topRentalMarkets(beds: "${bedrooms ?? ''}", propertyTypes: "${propertyTypes ?? ''}") {
            date
            areaName
            resourceId
            medianRent
            medianRentPriceOverTime {
                month
                year
                price
            }
        }
    }`.replace(/(^[ \t]*\n)/gm, ''); // finally remove empty lines;
};

export const getMedianRentByBedroomQuery = (resourceId: string) => {
  return `{
        studio: marketPage(resourceId: "${resourceId}", beds: "0", propertyTypes: "") {
            summary {
                medianRent
            }
        }
        oneBedroom: marketPage(resourceId: "${resourceId}", beds: "1", propertyTypes: "") {
            summary {
                medianRent
            }
        }
        twoBedroom: marketPage(resourceId: "${resourceId}", beds: "2", propertyTypes: "") {
            summary {
                medianRent
            }
        }
        threeBedroom: marketPage(resourceId: "${resourceId}", beds: "3", propertyTypes: "") {
            summary {
                medianRent
            }
        }
        fourPlusBedroom: marketPage(resourceId: "${resourceId}", beds: "4plus", propertyTypes: "") {
            summary {
                medianRent
            }
        }
    }`.replace(/(^[ \t]*\n)/gm, ''); // finally remove empty lines;
};
